/**
 * Simple Throttled Presence Manager - JavaScript Implementation
 * Manages presence tracking for Matrix users with throttled updates
 */

import { UserEvent } from 'matrix-js-sdk';
import logger from '../utils/logger';

// Simple throttle implementation (avoiding lodash dependency)
const throttle = (func, delay) => {
  let timeoutId;
  let lastExecTime = 0;
  let cancelled = false;
  
  const throttledFunction = (...args) => {
    const currentTime = Date.now();
    
    if (cancelled) return;
    
    if (currentTime - lastExecTime > delay) {
      // Execute immediately if enough time has passed
      lastExecTime = currentTime;
      func.apply(this, args);
    } else {
      // Schedule execution for later
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (!cancelled) {
          lastExecTime = Date.now();
          func.apply(this, args);
        }
      }, delay - (currentTime - lastExecTime));
    }
  };
  
  throttledFunction.cancel = () => {
    cancelled = true;
    clearTimeout(timeoutId);
  };
  
  return throttledFunction;
};

class PresenceManager {
  constructor(matrixClient) {
    this.client = matrixClient;
    
    // Core data structures
    this.presenceCache = new Map(); // userId -> presence data
    this.trackedUsers = new Set();  // Set of tracked user IDs
    this.callbacks = new Set();     // Set of callback functions
    this.pendingUsers = new Set();  // Users with pending updates
    
    // Throttled update function with 500ms delay
    this.throttledUpdate = throttle(() => {
      this.flush();
    }, 500);
    
    // Bind event listeners
    this.handlePresenceEvent = this.handlePresenceEvent.bind(this);
    this.handleCurrentlyActiveEvent = this.handleCurrentlyActiveEvent.bind(this);
    
    // Setup event listeners
    if (this.client) {
      this.client.on(UserEvent.Presence, this.handlePresenceEvent);
      this.client.on(UserEvent.CurrentlyActive, this.handleCurrentlyActiveEvent);
      logger.debug('PresenceManager: Event listeners attached');
    }
  }
  
  /**
   * Handle presence change events from Matrix
   */
  handlePresenceEvent(event, user) {
    const userId = user.userId;
    
    if (this.trackedUsers.has(userId)) {
      logger.debug(`PresenceManager: Presence event for ${userId}:`, event.getContent());
      this.updatePresence(userId, user);
    }
  }
  
  /**
   * Handle currently active events from Matrix
   */
  handleCurrentlyActiveEvent(event, user) {
    const userId = user.userId;
    
    if (this.trackedUsers.has(userId)) {
      logger.debug(`PresenceManager: Currently active event for ${userId}`);
      this.updatePresence(userId, user);
    }
  }
  
  /**
   * Update presence data for a user and trigger throttled callback
   */
  updatePresence(userId, user) {
    const presenceData = {
      userId: userId,
      presence: user.presence || 'offline',
      lastActiveAgo: user.lastActiveAgo || null,
      currentlyActive: user.currentlyActive || false,
      statusMsg: user.presenceStatusMsg || '',
      timestamp: Date.now(),
    };
    
    this.presenceCache.set(userId, presenceData);
    this.pendingUsers.add(userId);
    
    // Trigger throttled update
    this.throttledUpdate();
  }
  
  /**
   * Add a user to tracking with three-tier presence lookup
   */
  async trackUser(userId) {
    if (!userId) return;
    
    logger.debug(`PresenceManager: Starting to track user ${userId}`);
    this.trackedUsers.add(userId);
    
    // Get initial presence with fallback chain
    if (this.client) {
      const user = this.client.getUser(userId);
      
      if (user && user.presence && user.presence !== 'unknown') {
        // Tier 1: Found in Matrix SDK's memory cache
        logger.debug(`PresenceManager: Found cached presence for ${userId}: ${user.presence}`);
        this.updatePresence(userId, user);
      } else {
        // Tier 2 & 3: Try to fetch from server (SDK may check IndexedDB internally)
        logger.debug(`PresenceManager: No cached presence for ${userId}, fetching from server...`);
        
        // Fetch from server (async, non-blocking)
        this.fetchPresenceFromServer(userId).catch(err => {
          logger.debug(`PresenceManager: Server fetch failed for ${userId}:`, err.message);
        });
        
        // Set temporary state while fetching
        this.presenceCache.set(userId, {
          userId: userId,
          presence: 'unknown',
          lastActiveAgo: null,
          currentlyActive: false,
          statusMsg: '',
          timestamp: Date.now(),
        });
      }
    }
  }
  
  /**
   * Remove a user from tracking
   */
  untrackUser(userId) {
    logger.debug(`PresenceManager: Stopping tracking for user ${userId}`);
    this.trackedUsers.delete(userId);
    this.presenceCache.delete(userId);
    this.pendingUsers.delete(userId);
  }
  
  /**
   * Subscribe to presence updates
   * Returns unsubscribe function
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }
    
    this.callbacks.add(callback);
    logger.debug('PresenceManager: Added callback subscriber');
    
    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
      logger.debug('PresenceManager: Removed callback subscriber');
    };
  }
  
  /**
   * Flush pending updates to all subscribers
   */
  flush() {
    if (this.pendingUsers.size === 0) return;
    
    const updatedData = new Map();
    this.pendingUsers.forEach(userId => {
      const presence = this.presenceCache.get(userId);
      if (presence) {
        updatedData.set(userId, presence);
      }
    });
    
    logger.debug(`PresenceManager: Flushing updates for ${updatedData.size} users`);
    
    // Call all callbacks with updated data
    this.callbacks.forEach(callback => {
      try {
        callback(updatedData);
      } catch (error) {
        logger.error('PresenceManager: Error in callback:', error);
      }
    });
    
    // Clear pending users
    this.pendingUsers.clear();
  }
  
  /**
   * Get cached presence data for a user
   */
  getPresence(userId) {
    return this.presenceCache.get(userId) || null;
  }
  
  /**
   * Check if a user is considered online
   */
  isOnline(userId) {
    const presence = this.getPresence(userId);
    if (!presence) return false;
    
    // Treat 'unknown' state optimistically as potentially online for UI purposes
    // This prevents showing everyone as offline initially
    if (presence.presence === 'unknown') {
      // Check if we have any activity indicators
      return presence.currentlyActive === true;
    }
    
    return presence.presence === 'online' || presence.currentlyActive === true;
  }
  
  /**
   * Force refresh presence data for all tracked users
   * Uses server fetch for users without cached presence
   */
  refreshAllPresence() {
    if (!this.client) return;
    
    logger.debug(`PresenceManager: Refreshing presence for ${this.trackedUsers.size} tracked users`);
    
    this.trackedUsers.forEach(userId => {
      const user = this.client.getUser(userId);
      
      if (user && user.presence && user.presence !== 'unknown') {
        // Have valid cached data
        logger.debug(`PresenceManager: Refreshed presence for ${userId}: ${user.presence}`);
        this.updatePresence(userId, user);
      } else {
        // No valid cache - fetch from server
        logger.debug(`PresenceManager: Fetching fresh presence for ${userId} from server`);
        this.fetchPresenceFromServer(userId).catch(err => {
          logger.debug(`PresenceManager: Could not refresh presence for ${userId}:`, err.message);
        });
      }
    });
  }
  
  /**
   * Request presence information for a specific user from the server
   */
  async requestUserPresence(userId) {
    if (!this.client || !userId) return;
    
    try {
      // Try to get user profile which sometimes triggers presence updates
      await this.client.getProfileInfo(userId);
      logger.debug(`PresenceManager: Got profile for ${userId}, checking for presence update`);
      
      // Check if we now have presence data
      setTimeout(() => {
        const user = this.client.getUser(userId);
        if (user && user.presence && user.presence !== 'unknown') {
          logger.debug(`PresenceManager: Got presence after profile request for ${userId}: ${user.presence}`);
          this.updatePresence(userId, user);
        }
      }, 500);
    } catch (error) {
      logger.debug(`PresenceManager: Could not get profile/presence for ${userId}:`, error.message);
    }
  }

  /**
   * Fetch presence from server via API (three-tier fallback pattern)
   * 1. Memory cache (already checked by caller)
   * 2. Matrix SDK internal cache
   * 3. Server API call
   */
  async fetchPresenceFromServer(userId) {
    if (!this.client || !userId) return null;
    
    try {
      logger.debug(`PresenceManager: Fetching presence from server for ${userId}`);
      
      // Call Matrix API to get presence
      const presenceResponse = await this.client.getPresence(userId);
      
      if (!presenceResponse) {
        logger.debug(`PresenceManager: No presence data returned from server for ${userId}`);
        return null;
      }
      
      // Build presence data from server response
      const presenceData = {
        userId: userId,
        presence: presenceResponse.presence || 'offline',
        lastActiveAgo: presenceResponse.last_active_ago || null,
        currentlyActive: presenceResponse.currently_active || false,
        statusMsg: presenceResponse.status_msg || '',
        timestamp: Date.now(),
      };
      
      // Cache it locally
      this.presenceCache.set(userId, presenceData);
      this.pendingUsers.add(userId);
      
      // Notify subscribers via throttled update
      this.throttledUpdate();
      
      logger.debug(`PresenceManager: Got presence from server for ${userId}: ${presenceData.presence}`);
      return presenceData;
    } catch (error) {
      // This is expected for users who don't share presence
      logger.debug(`PresenceManager: Could not fetch presence from server for ${userId}:`, error.message);
      
      // Set as unavailable instead of leaving in unknown state
      const unavailableData = {
        userId: userId,
        presence: 'offline',
        lastActiveAgo: null,
        currentlyActive: false,
        statusMsg: '',
        timestamp: Date.now(),
      };
      
      this.presenceCache.set(userId, unavailableData);
      return null;
    }
  }

  /**
   * Set our own presence state on the Matrix server
   */
  async setOwnPresence(presence = 'online', statusMsg = '') {
    if (!this.client) return;
    
    try {
      logger.debug(`PresenceManager: Setting own presence to ${presence}`);
      await this.client.setPresence({
        presence: presence,
        status_msg: statusMsg,
      });
      
      // Also update our local cache immediately
      const userId = this.client.getUserId();
      if (userId) {
        this.updatePresence(userId, {
          userId: userId,
          presence: presence,
          currentlyActive: presence === 'online',
          lastActiveAgo: 0,
          statusMsg: statusMsg,
          getAvatarUrl: () => null,
        });
      }
    } catch (error) {
      logger.error('PresenceManager: Failed to set own presence:', error);
    }
  }

  /**
   * Get all tracked users' presence data for testing
   */
  getAllPresence() {
    const allPresence = {};
    this.presenceCache.forEach((presence, userId) => {
      allPresence[userId] = presence;
    });
    return allPresence;
  }
  
  /**
   * Cleanup and destroy the presence manager
   */
  destroy() {
    logger.debug('PresenceManager: Destroying presence manager');
    
    // Remove event listeners
    if (this.client) {
      this.client.removeListener(UserEvent.Presence, this.handlePresenceEvent);
      this.client.removeListener(UserEvent.CurrentlyActive, this.handleCurrentlyActiveEvent);
    }
    
    // Clear all data structures
    this.presenceCache.clear();
    this.trackedUsers.clear();
    this.callbacks.clear();
    this.pendingUsers.clear();
    
    // Cancel any pending throttled calls
    this.throttledUpdate.cancel();
  }
}

export default PresenceManager;